import { Flex } from 'antd'
import { MenuProps } from 'antd/lib'
import { Link } from 'react-router-dom'

import { usePatientTreatmentServiceGetPatientConsultationListEndpoint } from 'src/api/queries'
import { ConsultationStatus, Gender, Permissions } from 'src/api/requests'
import {
  HospitalIcon,
  BuildingIcon,
  UsersIcon,
  GraphIcon,
  GridEditIcon,
  NotificationStatusIcon,
  SettingIcon,
  DocumentCodeIcon,
  Calendar2Icon,
  HealthIcon,
  ClipboardTickIcon,
  NotificationIcon,
  StockIcon,
  PeopleIcon,
  ArrowIcon,
  Profile2UserIcon,
  CounterpartsIcon,
  UserTag,
  GlassIcon,
  TagUserIcon,
  SystemSetting,
  DocumentTextIcon,
  WomanIcon,
  ClipboardTextIcon,
  DevicesIcon
} from 'src/assets'
import { Text } from 'src/components'
import {
  MAX_PREGNANCY_AGE,
  MIN_PREGNANCY_AGE
} from 'src/features/persons/utils/'
import { getUserAge } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { useStyles } from './styles'
import { useUser } from './useUser'

type ExtendedMenuItem = {
  permission: string | string[]
  children?: ExtendedMenuItem[]
} & NonNullable<MenuProps['items']>[number]

//todo: when add dashboard
//IDENTITY_MENU_ACCESS_DASHBOARD_INSTITUTION
//IDENTITY_MENU_ACCESS_DASHBOARD_NATIONAL
//IDENTITY_MENU_ACCESS_DASHBOARD_PERSONAL

export const useNavigationItems = () => {
  const { t } = useLocales()

  const { styles } = useStyles()

  const { hasPermission, userData } = useUser()

  const { data } = usePatientTreatmentServiceGetPatientConsultationListEndpoint(
    [],
    {
      enabled: hasPermission(
        Permissions.TREATMENT_MENU_ACCESS_PERSONAL_TREATMENTS
      )
    }
  )

  const hasUpcomingConsultations = data?.data?.some(
    (item) => item?.status === ConsultationStatus.SCHEDULED
  )

  const shouldDisplayPregnancyMonitoring = () => {
    const gender = userData?.gender
    const age = getUserAge(userData?.dateOfBirth)

    return (
      gender === Gender.FEMALE &&
      age >= MIN_PREGNANCY_AGE &&
      age <= MAX_PREGNANCY_AGE
    )
  }

  const filterMenuItems = (items: ExtendedMenuItem[]): ExtendedMenuItem[] => {
    return items
      .map((item) => {
        const hasItemPermission =
          hasPermission(item.permission) ||
          (item.children?.some((child) => hasPermission(child.permission)) ??
            false)

        if (!hasItemPermission) return null

        if (
          item.key === 'pregnancy-monitoring' &&
          !shouldDisplayPregnancyMonitoring()
        ) {
          return null
        }

        return {
          ...item,
          children: item.children ? filterMenuItems(item.children) : undefined
        }
      })
      .filter(Boolean) as ExtendedMenuItem[]
  }

  const isAdminOrganization = hasPermission([
    Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_ORGANIZATIONAL_STRUCTURE,
    Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_ALL
  ])

  const items: ExtendedMenuItem[] = [
    {
      key: 'organizational-structure',
      label: (
        <Link
          to={
            isAdminOrganization
              ? paths.organizationalStructure.root
              : paths.organizationalStructure.details(
                  String(userData?.currentLegalEntityId)
                )
          }
        >
          <Text>{t('menu.organizational_structure')}</Text>
        </Link>
      ),
      icon: <HospitalIcon />,
      permission: [
        Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_ORGANIZATIONAL_STRUCTURE,
        Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_ALL,
        Permissions.ORGANIZATIONAL_STRUCTURE_ORGANIZATIONAL_STRUCTURE_VIEW_INSTITUTION,
        Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_INSTITUTION_ORGANIZATIONAL_STRUCTURE
      ]
    },
    {
      key: 'institutions',
      label: (
        <Link to={paths.institutions.root}>
          <Text>{t('menu.other_institutions')}</Text>
        </Link>
      ),
      icon: <BuildingIcon />,
      permission: Permissions.COUNTERPART_MENU_ACCESS_OTHER_INSTITUTIONS
    },
    {
      key: 'roles',
      label: (
        <Link to={paths.roles}>
          <Text>{t('menu.roles')}</Text>
        </Link>
      ),
      icon: <TagUserIcon />,
      permission: Permissions.IDENTITY_MENU_ACCESS_ROLES
    },
    {
      key: 'stockDropdown',
      label: <Text ellipsis>{t('menu.stock_institution')}</Text>,
      icon: <StockIcon />,
      children: [
        {
          key: 'stock',
          label: <Text>{t('menu.stock')}</Text>,
          children: [
            {
              key: 'stock-groups-medicines',
              label: (
                <Link to={paths.stock.stockGroupsMedicine}>
                  <Text>{t('common.medicines')}</Text>
                </Link>
              ),
              permission: Permissions.STOCKS_MENU_ACCESS_STOCKS
            },
            {
              key: 'stock-groups-pharmaceutics',
              label: (
                <Link to={paths.stock.stockGroupsPharmaceutics}>
                  <Text>{t('common.pharmaceutics')}</Text>
                </Link>
              ),
              permission: Permissions.STOCKS_MENU_ACCESS_STOCKS
            }
          ],
          permission: Permissions.STOCKS_MENU_ACCESS_STOCKS
        },
        {
          key: 'applications',
          label: (
            <Link to={paths.stock.applications}>
              <Text>{t('stock.applications')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS
        },
        {
          key: 'issuance',
          label: (
            <Link to={paths.issuance.root}>
              <Text>{t('menu.issuance')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_ISSUANCE
        },
        {
          key: 'inputs',
          label: (
            <Link to={paths.inputs.root}>
              <Text>{t('stock.inputs')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES
        },
        {
          key: 'returns',
          label: (
            <Link to={paths.stock.returns.root}>
              <Text>{t('stock.returns')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_STOCKS_RETURN
        },
        {
          key: 'settlements',
          label: (
            <Link to={paths.settlements.root}>
              <Text>{t('stock.settlement')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF
        }
      ],
      permission: [
        Permissions.STOCKS_MENU_ACCESS_STOCKS,
        Permissions.STOCKS_MENU_ACCESS_STOCKS_ENTRIES,
        Permissions.STOCKS_MENU_ACCESS_STOCKS_REQUESTS,
        Permissions.STOCKS_MENU_ACCESS_STOCKS_RETURN,
        Permissions.STOCKS_MENU_ACCESS_STOCKS_WRITE_OFF,
        Permissions.STOCKS_MENU_ACCESS_ISSUANCE
      ]
    },

    {
      key: 'counterparts',
      label: (
        <Link to={paths.counterpart.root}>
          <Text>{t('counterparts.title')}</Text>
        </Link>
      ),
      icon: <CounterpartsIcon />,
      permission: Permissions.COUNTERPART_MENU_ACCESS_COUNTERPARTS
    },
    {
      key: 'users',
      label: (
        <Link to={paths.users.root}>
          <Text>{t('menu.users')}</Text>
        </Link>
      ),
      icon: <UsersIcon />,
      permission: [Permissions.IDENTITY_MENU_ACCESS_USERS]
    },
    {
      key: 'tests',
      label: (
        <Link to={paths.tests.root}>
          <Text>{t('menu.tests')}</Text>
        </Link>
      ),
      icon: <GlassIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TESTS
    },
    {
      key: 'investigations',
      label: (
        <Link to={paths.investigations}>
          <Text>{t('menu.investigations')}</Text>
        </Link>
      ),
      icon: <GlassIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TEST_RESULTS
    },
    {
      key: 'test-results',
      label: (
        <Link to={paths.testsResults}>
          <Text>{t('menu.tests_results')}</Text>
        </Link>
      ),
      icon: <ClipboardTickIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_TEST_RESULTS
    },
    {
      key: 'equipments',
      label: (
        <Link to={paths.equipments.root}>
          <Text>{t('menu.equipments')}</Text>
        </Link>
      ),
      icon: <DevicesIcon />,
      permission: Permissions.ORGANIZATIONAL_STRUCTURE_MENU_ACCESS_EQUIPMENTS
    },
    {
      key: 'reports-administration',
      label: (
        <Link to={paths.reportsAdministration.root}>
          <Text>{t('menu.reports_administration')}</Text>
        </Link>
      ),
      icon: <GraphIcon />,
      permission: Permissions.REPORTS_MENU_ACCESS_REPORTS_MANAGEMENT
    },
    {
      key: 'classifiers menu',
      label: <Text>{t('menu.classifiers')}</Text>,
      icon: <GridEditIcon />,
      permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS,
      children: [
        {
          key: 'classifiers',
          label: (
            <Link to={paths.classifiers.root}>
              <Text>{t('menu.classifiers')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'cuatm',
          label: (
            <Link to={paths.cuatmClassifier.root}>
              <Text>{t('menu.cuatm')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'medicines-register',
          label: (
            <Link to={paths.medicinesRegister.root}>
              <Text>{t('menu.medicines_register')}</Text>
            </Link>
          ),
          permission:
            Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS_CONSUMABLE_MEDICINES
        },
        {
          key: 'investigation-templates',
          label: (
            <Link to={paths.tests.templates}>
              <Text>{t('menu.investigation_templates')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'device-types',
          label: (
            <Link to={paths.deviceTypes}>
              <Text>{t('menu.device_types')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'laboratory',
          label: (
            <Link to={paths.laboratoryClassifier.root}>
              <Text>{t('menu.laboratory_classifiers')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'stock-groups',
          label: (
            <Link to={paths.stock.stockGroups}>
              <Text>{t('menu.stock_groups')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_ISSUANCE
        },
        {
          key: 'treatment-types-classifier',
          label: (
            <Link to={paths.treatmentTypesClassifier.root}>
              <Text>{t('menu.treatment_types_classifier')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'funding-source',
          label: (
            <Link to={paths.fundingSource}>
              <Text>{t('menu.funding_source')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'risk-groups',
          label: (
            <Link to={paths.riskGroups.classifier}>
              <Text>{t('menu.risk_groups')}</Text>
            </Link>
          ),
          permission: Permissions.CLASSIFIER_MENU_ACCESS_CLASSIFIERS
        },
        {
          key: 'active-substances',
          label: (
            <Link to={paths.activeSubstances}>
              <Text>{t('menu.active_substances')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_MENU_ACCESS_ISSUANCE
        }
      ]
    },
    {
      key: 'persons-management',
      label: <Text ellipsis>{t('menu.persons_management')}</Text>,
      icon: <PeopleIcon />,
      permission: [
        Permissions.PERSONS_MENU_ACCESS_PERSONS,
        Permissions.PERSONS_MENU_ACCESS_PATIENTS
      ],
      children: [
        {
          key: 'persons',
          label: (
            <Link to={paths.persons.root}>
              <Text>{t('menu.persons')}</Text>
            </Link>
          ),
          permission: Permissions.PERSONS_MENU_ACCESS_PERSONS
        },
        {
          key: 'patients',
          label: (
            <Link to={paths.patients.root}>
              <Text>{t('menu.patients')}</Text>
            </Link>
          ),
          permission: Permissions.PERSONS_MENU_ACCESS_PATIENTS
        },
        {
          key: 'persons-from-screening',
          label: (
            <Link to={paths.personsFromScreening}>
              <Text>{t('menu.persons_from_screening')}</Text>
            </Link>
          ),
          permission: Permissions.PERSONS_MENU_ACCESS_PERSONS
        }
      ]
    },
    {
      key: 'risk-groups',
      label: (
        <Link to={paths.riskGroups.root}>
          <Text>{t('menu.ong_support')}</Text>
        </Link>
      ),
      icon: <UserTag />,
      permission: [Permissions.PRE_SCREENING_MENU_ACCESS_RISK_GROUPS]
    },
    {
      key: 'institution consultations',
      label: (
        <Link to={paths.institutionConsultations}>
          <Text>{t('persons.person_consultations')}</Text>
        </Link>
      ),
      icon: <Calendar2Icon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_CONSULTATIONS
    },
    {
      key: 'institution investigations',
      label: (
        <Link to={paths.institutionInvestigations}>
          <Text>{t('menu.tests')}</Text>
        </Link>
      ),
      icon: <GlassIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_INVESTIGATIONS
    },
    {
      key: 'screening-tb',
      label: (
        <Link to={paths.screeningTb}>
          <Text>{t('screening_tb.title')}</Text>
        </Link>
      ),
      icon: <ClipboardTickIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_SCREENING_TB_TAB
    },
    {
      key: 'institution epidemiology cases',
      label: (
        <Link to={paths.institutionEpidemiologyCases}>
          <Text>{t('persons.person_epidemiology-cases')}</Text>
        </Link>
      ),
      icon: <DocumentTextIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_EPIDEMIOLOGY_CASE
    },
    {
      key: 'institution treatments',
      label: (
        <Link to={paths.institutionTreatments}>
          <Text>{t('persons.person_treatments')}</Text>
        </Link>
      ),
      icon: <HealthIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_TREATMENTS
    },

    {
      key: 'monitoring',
      label: <Text ellipsis>{t('persons.person_pregnancy')}</Text>,
      icon: <WomanIcon />,
      children: [
        {
          key: 'institution-pregnancy',
          label: (
            <Link to={paths.institutionPregnancy}>
              <Text>{t('persons.person_pregnancy')}</Text>
            </Link>
          ),
          permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
        },
        {
          key: 'institution-births',
          label: (
            <Link to={paths.institutionBirths}>
              <Text>{t('pregnancy.births')}</Text>
            </Link>
          ),
          permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
        },
        {
          key: 'institution-investigation-pregnancy',
          label: (
            <Link to={paths.institutionPregnancyInvestigations}>
              <Text>{t('menu.investigations')}</Text>
            </Link>
          ),
          permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
        },
        {
          key: 'institution-newBorns',
          label: (
            <Link to={paths.institutionNewborns}>
              <Text>{t('pregnancy.newBorns')}</Text>
            </Link>
          ),
          permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
        },
        {
          key: 'institution-investigation-newBorns',
          label: (
            <Link to={paths.institutionPregnancyNewBornInvestigations}>
              <Text>{t('pregnancy.newBorns_investigations')}</Text>
            </Link>
          ),
          permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING
        }
      ],
      permission: [Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING]
    },
    {
      key: 'questionnaires',
      label: (
        <Link to={paths.questionnaires.root}>
          <Text>{t('menu.questionnaires')}</Text>
        </Link>
      ),
      icon: <NotificationStatusIcon />,
      permission: [
        Permissions.QUESTIONNAIRES_MENU_ACCESS_PERSONAL_QUESTIONNAIRES,
        Permissions.QUESTIONNAIRES_MENU_ACCESS_QUESTIONNAIRES
      ]
    },
    {
      key: 'stockLocal',
      label: <Text ellipsis>{t('menu.localStock')}</Text>,
      icon: <StockIcon />,
      children: [
        {
          key: 'local-stock',
          label: (
            <Link to={paths.stockLocal.stock}>
              <Text>{t('menu.stock')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL
        },
        {
          key: 'local-applications',
          label: (
            <Link to={paths.stockLocal.applications}>
              <Text>{t('stock.applications')}</Text>
            </Link>
          ),
          permission:
            Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS
        },
        {
          key: 'local-issuance',
          label: (
            <Link to={paths.stockLocal.issuance.root}>
              <Text>{t('menu.issuance')}</Text>
            </Link>
          ),
          permission: Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
        },
        {
          key: 'local-inputs',
          label: (
            <Link to={paths.stockLocal.inputs.root}>
              <Text>{t('stock.inputs')}</Text>
            </Link>
          ),
          permission:
            Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_ENTRIES
        },
        {
          key: 'local-returns',
          label: (
            <Link to={paths.stockLocal.returns.root}>
              <Text>{t('stock.returns')}</Text>
            </Link>
          ),
          permission:
            Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_RETURN
        }
      ],
      permission: [
        Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL,
        Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_REQUESTS,
        Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE,
        Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_ENTRIES,
        Permissions.STOCKS_INTERNAL_MENU_ACCESS_STOCKS_INTERNAL_RETURN
      ]
    },
    {
      key: 'institution recipes',
      label: (
        <Link to={paths.institutionPrescriptions}>
          <Text>{t('menu.prescriptions')}</Text>
        </Link>
      ),
      icon: <ClipboardTextIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_PRESCRIPTIONS
    },
    {
      key: 'notification-templates',
      label: (
        <Link to={paths.notificationTemplates.root}>
          <Text>{t('menu.notification_templates')}</Text>
        </Link>
      ),
      icon: <NotificationStatusIcon />,
      permission: Permissions.NOTIFICATIONS_MENU_ACCESS_NOTIFICATION_TEMPLATES
    },
    {
      key: 'systemSettings',
      label: (
        <Link to={paths.systemSettings}>
          <Text>{t('menu.systemSettings')}</Text>
        </Link>
      ),
      icon: <SystemSetting />,
      permission: Permissions.SETTINGS_MENU_ACCESS_SYSTEM_CONFIGURATIONS
    },
    /* PERSON   */
    {
      key: 'treatments',
      label: (
        <Link to={paths.treatments.root}>
          <Text>{t('menu.treatments')}</Text>
        </Link>
      ),
      icon: <HealthIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_PERSONAL_TREATMENTS
    },
    {
      key: 'appointments',
      label: (
        <Link to={paths.appointments}>
          <Flex gap={4}>
            <Text>{t('menu.appointments')}</Text>
            {hasUpcomingConsultations && <div className={styles.visits_dot} />}
          </Flex>
        </Link>
      ),
      icon: <Calendar2Icon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_PERSONAL_SCHEDULING
    },

    {
      key: 'person-tests-results',
      label: (
        <Link to={paths.personTestResults}>
          <Text>{t('menu.tests_results')}</Text>
        </Link>
      ),
      icon: <ClipboardTickIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_PERSONAL_TEST_RESULTS
    },
    {
      key: 'pregnancy-monitoring',
      label: (
        <Link to={paths.pregnancyMonitoring}>
          <Text>{t('persons.person_pregnancy')}</Text>
        </Link>
      ),
      icon: <WomanIcon />,
      permission:
        Permissions.TREATMENT_MENU_ACCESS_PERSONAL_PREGNANCY_MONITORING
    },
    {
      key: 'epidemiology-cases',
      label: (
        <Link to={paths.epidemiologyCases.root}>
          <Text>{t('menu.cases')}</Text>
        </Link>
      ),
      icon: <PeopleIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_EPIDEMIOLOGY_CASE
    },
    {
      key: 'transfers',
      label: (
        <Link to={paths.transfers}>
          <Text>{t('menu.transfers')}</Text>
        </Link>
      ),
      icon: <ArrowIcon />,
      permission: Permissions.PERSONS_MENU_ACCESS_PERSON_TRANSFER_REQUEST
    },

    {
      key: 'support-investigation',
      label: (
        <Link to={paths.supportInvestigation}>
          <Text>{t('menu.support_investigation')}</Text>
        </Link>
      ),
      icon: <Profile2UserIcon />,
      permission: Permissions.INVESTIGATIONS_MENU_ACCESS_INVESTIGATIONS_SUPPORT
    },
    {
      key: 'recipes',
      label: (
        <Link to={paths.prescriptions.root}>
          <Text>{t('menu.prescriptions')}</Text>
        </Link>
      ),
      icon: <ClipboardTextIcon />,
      permission: Permissions.TREATMENT_MENU_ACCESS_PRESCRIPTIONS
    },
    {
      key: 'templatesDropdown',
      label: <Text ellipsis>{t('menu.templates')}</Text>,
      icon: <DocumentCodeIcon />,
      permission: Permissions.TEMPLATES_MENU_VIEW_TEMPLATES,
      children: [
        {
          key: 'treatmentTemplates',
          label: (
            <Link to={paths.templates.treatments}>
              <Text>{t('menu.treatments')}</Text>
            </Link>
          ),
          permission: Permissions.TEMPLATES_MENU_VIEW_TEMPLATES
        }
      ]
    },
    {
      key: 'reports',
      label: (
        <Link to={paths.reports.root}>
          <Text>{t('menu.reports')}</Text>
        </Link>
      ),
      icon: <NotificationStatusIcon />,
      permission: Permissions.REPORTS_MENU_ACCESS_REPORTS
    },
    {
      key: 'notifications',
      label: (
        <Link to={paths.notifications}>
          <Text>{t('menu.notifications')}</Text>
        </Link>
      ),
      icon: <NotificationIcon />,
      permission: Permissions.NOTIFICATIONS_MENU_ACCESS_NOTIFICATIONS
    },
    {
      key: 'logs',
      label: (
        <Link to={paths.logs}>
          <Text>{t('menu.logs')}</Text>
        </Link>
      ),
      icon: <DocumentCodeIcon />,
      permission: Permissions.NOTIFICATIONS_MENU_ACCESS_ACTIVITIES
    },
    {
      key: 'settings',
      label: (
        <Link to={paths.settings}>
          <Text>{t('menu.settings')}</Text>
        </Link>
      ),
      icon: <SettingIcon />,
      permission: Permissions.IDENTITY_MENU_ACCESS_SETTINGS
    }
  ]

  const filteredItems = filterMenuItems(items)

  return filteredItems
}
